import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import {
    BrandMark
} from "./brand";
import {
    NCoreUIKitTheme,
    Separator,
    TextInput,
    AreaChart,
    StatTile,
    Progress,
    Button,
    Avatar,
    Chart,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    Building2,
    KeyRound,
    Mail
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

const MailIcon = createKitIcon(Mail);

const PasskeyIcon = createKitIcon(KeyRound);

const SSOIcon = createKitIcon(Building2);

const CURRENT = [
    34,
    38,
    37,
    45,
    52,
    50,
    61,
    70,
    66,
    78,
    88,
    96
];

const PREVIOUS = [
    30,
    31,
    35,
    34,
    40,
    43,
    42,
    49,
    52,
    55,
    58,
    63
];

const TREND = [
    10,
    14,
    12,
    18,
    22,
    20,
    26,
    31
];

const ROWS = CURRENT.map((current, index) => ({
    previous: PREVIOUS[index] ?? 0,
    week: index + 1,
    current
}));

const stylesheet = StyleSheet.create({
    inline: {
        justifyContent: "center",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    formArea: {
        justifyContent: "center",
        alignItems: "center",
        flexGrow: 1
    },
    previewArea: {
        justifyContent: "center",
        alignItems: "center",
        flexGrow: 1
    },
    column: {
        flexDirection: "column"
    },
    heading: {
        flexDirection: "column"
    },
    legalRow: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    person: {
        flexDirection: "row",
        alignItems: "center"
    },
    page: {
        flexDirection: "row",
        flexGrow: 1
    },
    tiles: {
        flexDirection: "row"
    },
    centered: {
        textAlign: "center"
    },
    preview: {
        width: "100%"
    },
    form: {
        width: "100%"
    },
    previewColumn: {
        flex: 1
    },
    formColumn: {
        flex: 1
    },
    tile: {
        flex: 1
    }
});

const Login02 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const isWide = width >= shapeConfigs.wideBreakpoint;

    const handlePress = () => undefined;

    const handleCategoryFormat = (value: number | string) => `${t("blocks.login02.week")} ${value}`;

    return <View
        style={stylesheet.page}
    >
        <View
            style={[
                stylesheet.formColumn,
                {
                    padding: spaces.spacingLg,
                    gap: spaces.spacingLg
                }
            ]}
        >
            <BrandMark/>
            <View
                style={stylesheet.formArea}
            >
                <View
                    style={[
                        stylesheet.form,
                        {
                            maxWidth: shapeConfigs.cardWidth,
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.heading,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Text
                            variant="headlineMediumSize"
                            color="high"
                        >
                            {t("blocks.login02.title")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.login02.description")}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.column,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <TextInput
                            placeholder={t("blocks.login02.emailPlaceholder")}
                            defaultValue={t("blocks.login02.emailValue")}
                            title={t("blocks.login02.email")}
                            keyboardType="email-address"
                            spreadBehaviour="stretch"
                            autoComplete="email"
                            inputMode="email"
                            icon={MailIcon}
                        />
                        <Button
                            title={t("blocks.login02.submit")}
                            spreadBehaviour="stretch"
                            onPress={handlePress}
                            variant="filled"
                            type="primary"
                        />
                    </View>
                    <Separator
                        title={t("blocks.login02.or")}
                        isShowTitle={true}
                    />
                    <View
                        style={[
                            stylesheet.column,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Button
                            title={t("blocks.login02.passkey")}
                            spreadBehaviour="stretch"
                            onPress={handlePress}
                            icon={PasskeyIcon}
                            variant="outline"
                            type="neutral"
                        />
                        <Button
                            title={t("blocks.login02.sso")}
                            spreadBehaviour="stretch"
                            onPress={handlePress}
                            variant="outline"
                            icon={SSOIcon}
                            type="neutral"
                        />
                    </View>
                    <View
                        style={[
                            stylesheet.inline,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.login02.noAccount")}
                        </Text>
                        <Button
                            title={t("blocks.login02.trial")}
                            spreadBehaviour="free"
                            onPress={handlePress}
                            variant="link"
                            type="primary"
                        />
                    </View>
                </View>
            </View>
            <View
                style={[
                    stylesheet.legalRow,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    variant="bodySmallSize"
                    color="mid"
                >
                    {t("blocks.login02.copyright")}
                </Text>
                <Button
                    title={t("blocks.login02.privacy")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="link"
                    type="neutral"
                    size="small"
                />
                <Button
                    title={t("blocks.login02.terms")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="link"
                    type="neutral"
                    size="small"
                />
            </View>
        </View>
        {isWide && <Card
            style={[
                stylesheet.previewColumn,
                {
                    margin: spaces.spacingSm
                }
            ]}
            variant="filled"
        >
            <Card.Content
                style={stylesheet.previewArea}
            >
                <View
                    style={[
                        stylesheet.preview,
                        {
                            maxWidth: shapeConfigs.contentMaxWidth,
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    <Chart
                        config={{
                            previous: {
                                title: t("blocks.login02.previousPeriod"),
                                colorIndex: 3
                            },
                            current: {
                                title: t("blocks.login02.thisPeriod"),
                                colorIndex: 1
                            }
                        }}
                        description={t("blocks.login02.chartDescription")}
                        title={t("blocks.login02.chartTitle")}
                        spreadBehaviour="stretch"
                        isAnimatedOnMount={true}
                    >
                        <AreaChart
                            categoryFormatter={handleCategoryFormat}
                            stackBehaviour="overlay"
                            categoryKey="week"
                            plotSize="small"
                            curve="natural"
                            data={ROWS}
                        />
                    </Chart>
                    <View
                        style={[
                            stylesheet.tiles,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <StatTile
                            style={stylesheet.tile}
                            variant="outline"
                            trend={TREND}
                        >
                            <StatTile.Title>
                                {t("blocks.login02.revenue")}
                            </StatTile.Title>
                            <StatTile.Value>
                                {t("blocks.login02.revenueValue")}
                            </StatTile.Value>
                            <StatTile.Delta
                                description={t("blocks.login02.revenueDescription")}
                                value={18.4}
                            />
                        </StatTile>
                        <StatTile
                            style={stylesheet.tile}
                            variant="outline"
                        >
                            <StatTile.Title>
                                {t("blocks.login02.customers")}
                            </StatTile.Title>
                            <StatTile.Value>
                                {t("blocks.login02.customersValue")}
                            </StatTile.Value>
                            <Progress
                                accessibilityLabel={t("blocks.login02.customers")}
                                description={t("blocks.login02.goal")}
                                size="small"
                                value={74}
                            />
                        </StatTile>
                    </View>
                    <View
                        style={[
                            stylesheet.column,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text
                            variant="readingMediumSize"
                            color="high"
                        >
                            {t("blocks.login02.quote")}
                        </Text>
                        <View
                            style={[
                                stylesheet.person,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Avatar
                                title={t("blocks.login02.person")}
                                type="neutral"
                                size="small"
                            />
                            <View>
                                <Text
                                    variant="labelMediumSize"
                                    color="high"
                                >
                                    {t("blocks.login02.person")}
                                </Text>
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t("blocks.login02.role")}
                                </Text>
                            </View>
                        </View>
                    </View>
                </View>
            </Card.Content>
        </Card>}
    </View>;
};
export default Login02;
